import moment from 'moment'
import { ClientInfoStatus } from '../../interfaces/client'
import { CollectionType } from '../../interfaces/collection'
import { FilterFieldType, IFilter } from './types'

export const COLLECTIONS_LIST_FILTERS_CONFIG = [
  {
    field: 'clients',
    dbField: 'client_name',
    type: FilterFieldType.Autocomplete,
    isSortable: true,
    placeholder: 'Client',
    title: 'Client',
  },
  {
    field: 'clientStatus',
    dbField: 'client_status',
    type: FilterFieldType.List,
    isMultiple: true,
    title: 'Client Status',
    excludeColumn: true,
    options: [
      {
        value: ClientInfoStatus.Current,
        label: ClientInfoStatus.Current,
      },
      {
        value: ClientInfoStatus.Past,
        label: ClientInfoStatus.Past,
      },
      {
        value: ClientInfoStatus.PastArchived,
        label: ClientInfoStatus.PastArchived,
      },
    ],
  },
  {
    field: 'recordDate',
    dbField: 'record_date',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Date',
    excludeColumn: true,
    isExternalField: true,
  },
  {
    field: 'debtor',
    type: FilterFieldType.Autocomplete,
    placeholder: 'Customer',
    title: 'Customer',
    excludeColumn: true,
  },
  {
    field: 'paymentType',
    type: FilterFieldType.List,
    options: Object.values(CollectionType).map((type) => ({
      value: type,
      label: type,
    })),
    title: 'Type',
    excludeColumn: true,
  },
  {
    field: 'count',
    type: FilterFieldType.Number,
    title: 'Count',
    aggColumn: true,
    isSortable: true,
    dbField: 'COUNT("paymentType")',
  },
  {
    field: 'checksPaymentAmount',
    type: FilterFieldType.Amount,
    title: 'Check amount',
    align: 'right',
    isSortable: true,
    aggColumn: true,
    dbField: 'ROUND(SUM(checks_payment_amount)::numeric, 2)',
  },
  {
    field: 'wiresPaymentAmount',
    type: FilterFieldType.Amount,
    title: 'Wire amount',
    align: 'right',
    isSortable: true,
    aggColumn: true,
    dbField: 'ROUND(SUM(wires_payment_amount)::numeric, 2)',
  },
  {
    field: 'totalPaymentAmount',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total amount',
    align: 'right',
    aggColumn: true,
    dbField: 'ROUND(SUM(checks_payment_amount + wires_payment_amount)::numeric, 2)',
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    fields: [
      {
        field: 'clientStatus',
        type: FilterFieldType.List,
      },
    ],
    quickFilters: [
      {
        title: 'Today',
        filters: {
          recordDateFrom: moment().format('YYYY-MM-DD'),
          recordDateTo: moment().format('YYYY-MM-DD'),
        },
      },
      {
        title: 'Current Clients',
        filters: {
          clientStatus: [ClientInfoStatus.Current],
        },
      },
    ],
  },
] as const satisfies IFilter[]

export const COLLECTIONS_RECONCILIATION_LIST_FILTERS_CONFIG = [
  {
    field: 'label',
    type: FilterFieldType.Text,
    title: 'Customer',
    align: 'left',
    isSortable: true,
  },
  {
    field: 'implied_collections',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Implied Collections',
  },
  {
    field: 'ar_gl_payments',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'AR GL Payments',
  },
  {
    field: 'actual_collections',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Actual Collections',
  },
  {
    field: 'ar_gl_invoices',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'AR GL Invoices',
  },
  {
    field: 'ar_gl_dilutive_credits',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'AR GL Dilutive Credits',
  },
  {
    field: 'ar_gl_other',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'AR GL Other',
  },
  {
    field: 'ar_gl_dilution',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'AR GL Dilution',
  },
  {
    field: 'actual_dilution',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Actual Dilution',
  },
  {
    field: 'dilution_change',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: '% Change',
  },
] as const satisfies IFilter[]

export const COLLECTION_ROW_FILTERS_CONFIG = [
  {
    field: 'debtor',
    title: 'Debtor',
    type: FilterFieldType.Text,
    isSortable: true,
  },
  {
    field: 'paymentType',
    title: 'Type',
    type: FilterFieldType.Text,
    isSortable: true,
  },
  {
    field: 'recordDate',
    title: 'Record Date',
    type: FilterFieldType.Date,
    isSortable: true,
    alias: 'data',
  },
  {
    field: 'description',
    title: 'Description',
    type: FilterFieldType.Text,
    isSortable: true,
  },
  {
    field: 'amount',
    title: 'Amount',
    type: FilterFieldType.Amount,
    isSortable: true,
  },
  {
    field: 'action',
    title: 'Action',
    type: FilterFieldType.Empty,
  },
] as const satisfies IFilter[]

export const CHECKS_FILTERS_CONFIG = [
  {
    field: 'checkNumber',
    type: FilterFieldType.Text,
    dbField: 'check_number',
    isSortable: true,
    title: 'Check #',
  },
  {
    field: 'clientName',
    type: FilterFieldType.Text,
    dbField: 'client_name',
    isSortable: true,
    title: 'Client',
    alias: 'clientInfo',
  },
  {
    field: 'clientConfidence',
    dbField: 'client_confidence',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Confidence',
    alias: 'checks',
  },
  {
    field: 'debtor',
    dbField: 'name',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Customer',
    alias: 'entityInfo',
  },
  {
    field: 'originalEntity',
    dbField: 'original_entity',
    type: FilterFieldType.Text,
    title: 'Primary Customer',
  },
  {
    field: 'paymentAmount',
    dbField: 'payment_amount',
    type: FilterFieldType.Amount,
    title: 'Amount',
    align: 'right',
    isSortable: true,
  },
  {
    field: 'action',
    type: FilterFieldType.Empty,
    title: 'Action',
  },
] as const satisfies IFilter[]

export const WIRE_MAPPING_FILTERS_CONFIG = [
  {
    field: 'recordDate',
    dbField: 'record_date',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Date',
    align: 'left',
    isExternalField: true,
  },
  {
    field: 'originalMemo',
    dbField: 'original_memo',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Memo',
    alias: 'wires',
  },
  {
    field: 'clientName',
    dbField: 'client_name',
    type: FilterFieldType.Autocomplete,
    isSortable: true,
    title: 'Client',
    alias: 'wires',
  },
  {
    field: 'debtor',
    dbField: 'name',
    type: FilterFieldType.Autocomplete,
    isSortable: true,
    title: 'Customer',
    alias: 'entityInfo',
  },
  {
    field: 'prediction',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Prediction',
  },
  {
    field: 'accountNumber',
    dbField: 'account_number',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Account number',
    alias: 'wires',
    align: 'right',
  },
  {
    field: 'amount',
    dbField: 'payment_amount',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Amount',
    alias: 'wires',
  },
  {
    field: 'isPotentialDuplicate',
    dbField: 'is_potential_duplicate',
    excludeFullSearchFilter: true,
    type: FilterFieldType.Boolean,
    isSortable: true,
    title: 'Potential Duplicate',
    alias: 'wires',
    options: [
      {
        value: 'true',
        label: 'Yes',
      },
      {
        value: 'false',
        label: 'No',
      },
    ],
  },
  {
    field: 'action',
    type: FilterFieldType.Empty,
    title: 'Action',
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      {
        title: 'Potential Duplicates',
        filters: {
          isPotentialDuplicate: true,
        },
      },
    ],
  },
] as const satisfies IFilter[]

export const DELETED_COLLECTIONS_LIST_FILTERS_CONFIG = [
  {
    field: 'description',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Description',
    title: 'Description',
  },
  {
    field: 'credit',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Credit',
  },
  {
    field: 'debit',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Debit',
  },
  {
    field: 'originalRecordDate',
    dbField: 'original_record_date',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Received date',
    isExternalField: true,
  },
  {
    field: 'recordDate',
    dbField: 'record_date',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Returned date',
  },
  {
    field: 'status',
    type: FilterFieldType.List,
    isMultiple: true,
    options: [
      {
        value: 'all',
        label: 'All',
      },
      {
        value: 'sent',
        label: 'Returned',
      },
      {
        value: 'notsent',
        label: 'Unreturned',
      },
    ],
    title: 'Action',
    align: 'right',
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      {
        title: 'Unreturned',
        filters: {
          status: ['notsent'],
        },
      },
      {
        title: 'Returned Today',
        filters: {
          recordDateFrom: moment().format('YYYY-MM-DD'),
          recordDateTo: moment().format('YYYY-MM-DD'),
        },
      },
      {
        title: 'Received Today',
        filters: {
          originalRecordDateFrom: moment().format('YYYY-MM-DD'),
          originalRecordDateTo: moment().format('YYYY-MM-DD'),
        },
      },
    ],
  },
] as const satisfies IFilter[]
